import { useState } from 'react';
import { Flex, Box, Avatar } from '@chakra-ui/react';
import { Atom01 } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components';
import { SessionTranscriptProps, TranscriptEntry } from '../types';

const TranscriptEntryItem = ({
  entry,
  isAgent,
  candidateName,
  candidateAvatarUrl,
}: {
  entry: TranscriptEntry;
  isAgent: boolean;
  candidateName?: string;
  candidateAvatarUrl?: string;
}) => {
  const displayName = isAgent ? 'AI Coordinator' : candidateName || 'Candidate';
  return (
    <Flex w="100%" mb="8px" alignItems="flex-start" gap="12px">
      {isAgent ? (
        <Flex
          w="24px"
          h="24px"
          borderRadius="4px"
          flexShrink={0}
          alignItems="center"
          justifyContent="center"
          bg="secondary-950"
        >
          <Atom01
            size={14}
            color="var(--kula-colors-base-white)"
            className="kula-icon"
          />
        </Flex>
      ) : (
        <Avatar
          size="xs"
          name={displayName}
          src={candidateAvatarUrl}
          borderRadius="4px"
          flexShrink={0}
        />
      )}
      <Box flex={1} minW={0}>
        <KText size="sm-medium" color="base-black" mb="4px">
          {displayName}
        </KText>
        <KText size="sm-regular" color="secondary-900" lineHeight="1.5">
          {entry.text}
        </KText>
      </Box>
    </Flex>
  );
};

export const AISessionTranscript: React.FC<SessionTranscriptProps> = ({
  transcript,
  maxVisible = 3,
  candidateName,
  candidateAvatarUrl,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!transcript || transcript.length === 0) {
    return (
      <Flex
        p="16px"
        bg="secondary-25"
        borderRadius="8px"
        justifyContent="center"
      >
        <KText size="sm-regular" color="secondary-500">
          No transcript available
        </KText>
      </Flex>
    );
  }

  const hasMore = transcript.length > maxVisible;
  const visibleEntries = isExpanded
    ? transcript
    : transcript.slice(0, maxVisible);

  return (
    <Flex flexDir="column" w="100%">
      <Box position="relative">
        {visibleEntries.map((entry, index) => (
          <TranscriptEntryItem
            key={index}
            entry={entry}
            isAgent={entry.speaker === 'agent'}
            candidateName={candidateName}
            candidateAvatarUrl={candidateAvatarUrl}
          />
        ))}
      </Box>

      {hasMore && (
        <Flex justifyContent="center" mt="8px">
          <KText
            size="sm-medium"
            color="primary-500"
            cursor="pointer"
            onClick={() => setIsExpanded(!isExpanded)}
            _hover={{ textDecoration: 'underline' }}
          >
            {isExpanded
              ? 'Show less'
              : `Show ${transcript.length - maxVisible} more`}
          </KText>
        </Flex>
      )}
    </Flex>
  );
};
